import { LayerBitMask } from '../layers/Layer';
import { PlaneView } from '../views/PlaneView';

/**
 * {@link ViewUpdateEvent} provides a common interface for different types of {@link ViewUpdateEvent ViewUpdateEvents}.\
 * {@link View Views} are updated using {@link ViewUpdateEvent ViewUpdateEvents}.\
 * {@link ViewUpdateEvent ViewUpdateEvents} describe a change that might trigger some kind of update in the visualization.
 *
 * We may specify which layers the {@link ViewUpdateEvent} affects, such as the {@link ContourLayer}.
 */
export abstract class ViewUpdateEvent {
    /**
     * Bitflag controlling the {@link Layer Layers} this {@link ViewUpdateEvent} affects.
     */
    public readonly affectedLayers: LayerBitMask;

    /**
     * Whether the contents of {@link View}s' {@link TextureView.texture textures} should be updated.\
     * Generally always true.\
     * Only false if only {@link Filter.postProcessing filter's post-processing} should be updated.
     */
    protected readonly updateContent: boolean;

    /**
     * {@link ViewUpdateEvent} constructor.
     * @param eventFlags Bitflag of additional operations that need to be performed when processing the event.
     * @param affectedLayers Bitflag controlling the {@link Layer Layers} affected by this event.
     * @param updateContent Boolean flag that controls whether {@link Layer} contents should be updated.\
     * True if {@link Layer} contents need to be updated. This is generally always the case.\
     * False if only the {@link Filter} needs to be updated.
     */
    constructor(affectedLayers: number, updateContent: boolean) {
        this.affectedLayers = affectedLayers;
        this.updateContent = updateContent;
    }

    /**
     * Method that will update {@link View Views}.\
     * This method should inspect whether the {@link view} is affected by the {@link ViewUpdateEvent event}\
     * and update it's contents accordingly. Returning true if the {@link view} was updated.
     * @param view The {@link View} that might be updated.
     * @returns True if {@link View} was updated as a result of this {@link ViewUpdateEvent}. False otherwise.
     */
    public updateView(view: PlaneView): boolean {
        if (this.shouldUpdateView(view)) {
            view.updateView(this.affectedLayers);
        }
        return false;
    }

    /**
     * Abstract method to inspect if this {@link ViewUpdateEvent} should update a {@link View}.
     * @param view The {@link View} that might be updated.
     * @returns True if the {@link view} should be updated, false otherwise.
     */
    public abstract shouldUpdateView(view: PlaneView): boolean;

    /**
     * Getter to determine if {@link View's} content should be updated.
     * @returns True if the View's contents need updating. False if only the {@link Filter} is to be updated.
     */
    public get shouldUpdateContent(): boolean {
        return this.updateContent;
    }
}
